/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { RefObject } from 'react';
import {
  Behavior,
  DataRecord,
  FilterState,
  QueryFormData,
} from '@superset-ui/core';
import { PluginFilterHooks, PluginFilterStylesProps } from '../types';
import { FilterBarOrientation } from '../../../dashboard/types';

/**
 * Quick selection options for date range
 */
export enum QuickSelectionType {
  NONE = '',
  CUSTOM = 'custom',
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  THIS_WEEK = 'this_week',
  LAST_WEEK = 'last_week',
  THIS_MONTH = 'this_month',
  LAST_MONTH = 'last_month',
  THIS_QUARTER = 'this_quarter',
  LAST_QUARTER = 'last_quarter',
  THIS_YEAR = 'this_year',
  LAST_YEAR = 'last_year',
}

interface PluginFilterDatePickerCustomizeProps {
  /** Default days offset from today (e.g., 7 means last 7 days) */
  defaultDaysOffset?: number;
  /** Date format string */
  dateFormat?: string;
  /** Enable quick selection dropdown */
  enableQuickSelection?: boolean;
}

export type PluginFilterDatePickerQueryFormData = QueryFormData &
  PluginFilterStylesProps &
  PluginFilterDatePickerCustomizeProps;

export type PluginFilterDatePickerProps = PluginFilterStylesProps & {
  behaviors: Behavior[];
  data: DataRecord[];
  formData: PluginFilterDatePickerQueryFormData;
  filterState: FilterState;
  ownState?: Record<string, unknown>;
  inputRef: RefObject<any>;
  filterBarOrientation?: FilterBarOrientation;
  isOverflowingFilterBar?: boolean;
  showOverflow?: boolean;
  parentRef?: RefObject<any>;
} & PluginFilterHooks;

export const DEFAULT_FORM_DATA: PluginFilterDatePickerCustomizeProps = {
  defaultDaysOffset: undefined,
  dateFormat: 'YYYY-MM-DD',
  enableQuickSelection: false,
};
